import React from 'react';
import { reportingApi } from '../api';
import { formatReportContextInsertion, summarizeReportContext } from '../reporting/reportContextFormatter';
import { Box, Button, Chip, CircularProgress, Divider, MenuItem, TextField, Typography } from '@mui/material';

const emptyReport = {
  clinicalHistory: '',
  technique: '',
  findings: '',
  impression: '',
  status: 'draft',
  hasReport: false,
};

const fieldSx = {
  '& .MuiInputBase-root': { color: '#d8e6ea', fontSize: 12, bgcolor: '#0b0f11' },
  '& .MuiInputLabel-root': { color: '#607d8b', fontSize: 11 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#1f333a' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#34545f' },
};

const NativeReportPanel = ({ studyInstanceUID, studyInfo, user, activeReportContext }) => {
  const [report, setReport] = React.useState(emptyReport);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [templates, setTemplates] = React.useState([]);
  const [history, setHistory] = React.useState([]);
  const [amendmentMode, setAmendmentMode] = React.useState(false);

  React.useLayoutEffect(() => {
    setReport(emptyReport);
    setHistory([]);
    setLoading(true);
    setSaving(false);
    setError('');
    setMessage('');
    setAmendmentMode(false);
  }, [studyInstanceUID]);

  React.useEffect(() => {
    if (!studyInstanceUID) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setMessage('');
    reportingApi.get(studyInstanceUID)
      .then(data => {
        if (cancelled) return;
        setReport({ ...emptyReport, ...data });
      })
      .catch(e => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message || 'Could not load report');
        setReport(emptyReport);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studyInstanceUID]);

  React.useEffect(() => {
    reportingApi.templates({ modality: studyInfo?.modalities?.split('/')?.[0] || undefined })
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, [studyInfo?.modalities]);

  const loadHistory = React.useCallback(() => {
    if (!studyInstanceUID) return;
    reportingApi.history(studyInstanceUID)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [studyInstanceUID]);

  React.useEffect(() => { loadHistory(); }, [loadHistory, report.updatedAt, report.status]);

  const update = (field) => (event) => {
    setReport(prev => ({ ...prev, [field]: event.target.value }));
    setMessage('');
  };

  const payload = () => ({
    clinicalHistory: report.clinicalHistory || '',
    technique: report.technique || '',
    findings: report.findings || '',
    impression: report.impression || '',
    author: user?.username || user?.id || 'viewer',
  });

  const save = async (finalize = false) => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const next = finalize
        ? await reportingApi.finalize(studyInstanceUID, payload())
        : await reportingApi.saveDraft(studyInstanceUID, payload());
      setReport({ ...emptyReport, ...next });
      setMessage(finalize ? 'Report finalized' : 'Draft saved');
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Could not save report');
    } finally {
      setSaving(false);
    }
  };

  const amend = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const next = await reportingApi.amend(studyInstanceUID, payload());
      setReport({ ...emptyReport, ...next });
      setMessage('Amendment signed');
      setAmendmentMode(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Could not amend report');
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = (templateId) => {
    const tmpl = templates.find(t => t.id === templateId);
    if (!tmpl) return;
    setReport(prev => ({
      ...prev,
      clinicalHistory: tmpl.clinicalHistory || prev.clinicalHistory || '',
      technique: tmpl.technique || prev.technique || '',
      findings: tmpl.findings || prev.findings || '',
      impression: tmpl.impression || prev.impression || '',
    }));
    setMessage(`Applied template: ${tmpl.name}`);
  };

  const insertHelper = async (text, target = 'findings') => {
    if (!studyInstanceUID) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const next = await reportingApi.insertText(studyInstanceUID, { target, text, author: user?.username || user?.id || 'viewer' });
      setReport({ ...emptyReport, ...next });
      setMessage('Inserted into report');
    } catch (e) {
      // If no persisted draft exists yet, keep the workflow moving by appending locally.
      setReport(prev => ({ ...prev, [target]: `${prev[target] || ''}${prev[target] ? '\n' : ''}${text}` }));
      setMessage('Inserted locally; save draft to persist');
    } finally {
      setSaving(false);
    }
  };

  const isSigned = report.status === 'final' || report.status === 'amended';
  const readOnly = isSigned && !amendmentMode;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#0a0d0f', borderLeft: '1px solid #17242a' }}>
      <Box sx={{ px: 1.25, py: 1, borderBottom: '1px solid #17242a' }}>
        <Typography sx={{ fontSize: 11, color: '#00bcd4', letterSpacing: 1.5, fontWeight: 'bold' }}>
          NATIVE REPORT
        </Typography>
        <Typography sx={{ fontSize: 10, color: '#6d7f86', mt: 0.5 }} noWrap>
          {studyInfo?.patientName || 'Unknown Patient'} · {studyInfo?.accessionNumber || 'No Accession'}
        </Typography>
        <Box sx={{ mt: 0.75, display: 'flex', gap: 0.75, alignItems: 'center' }}>
          <Chip size="small" label={(report.status || 'draft').toUpperCase()} color={isSigned ? 'success' : 'default'} sx={{ height: 18, fontSize: 9 }} />
          {report.updatedAt && <Typography sx={{ fontSize: 9, color: '#455a64' }}>updated {new Date(report.updatedAt).toLocaleString()}</Typography>}
        </Box>
      </Box>

      <Box sx={{ p: 1, overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {loading ? <CircularProgress size={18} /> : null}
        {error ? <Typography sx={{ fontSize: 11, color: '#ff8a80' }}>{error}</Typography> : null}
        {message ? <Typography sx={{ fontSize: 11, color: '#80cbc4' }}>{message}</Typography> : null}
        {templates.length > 0 ? (
          <TextField select label="Template / macro" defaultValue="" size="small" onChange={(e) => applyTemplate(e.target.value)} disabled={readOnly} sx={fieldSx}>
            <MenuItem value="">Select template…</MenuItem>
            {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
        ) : null}
        <Box sx={{ p: 0.75, border: '1px solid #17242a', bgcolor: '#071012' }}>
          <Typography sx={{ fontSize: 9, color: '#78909c', mb: 0.75 }} noWrap>{summarizeReportContext(activeReportContext)}</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" disabled={saving || loading || readOnly || !activeReportContext?.measurement} onClick={() => insertHelper(formatReportContextInsertion({ ...activeReportContext, kind: 'measurement' }), 'findings')} sx={{ fontSize: 9, borderRadius: 0 }}>Insert Measurement</Button>
            <Button size="small" variant="outlined" disabled={saving || loading || readOnly || !activeReportContext?.roi} onClick={() => insertHelper(formatReportContextInsertion({ ...activeReportContext, kind: 'roi' }), 'findings')} sx={{ fontSize: 9, borderRadius: 0 }}>Insert ROI</Button>
            <Button size="small" variant="outlined" disabled={saving || loading || readOnly || !activeReportContext?.series} onClick={() => insertHelper(formatReportContextInsertion({ ...activeReportContext, kind: 'keyImage' }), 'findings')} sx={{ fontSize: 9, borderRadius: 0 }}>Insert Key Image</Button>
          </Box>
        </Box>
        <TextField label="Clinical history" value={report.clinicalHistory || ''} onChange={update('clinicalHistory')} multiline minRows={2} size="small" disabled={readOnly} sx={fieldSx} />
        <TextField label="Technique" value={report.technique || ''} onChange={update('technique')} multiline minRows={2} size="small" disabled={readOnly} sx={fieldSx} />
        <TextField label="Findings" value={report.findings || ''} onChange={update('findings')} multiline minRows={7} size="small" disabled={readOnly} sx={fieldSx} />
        <TextField label="Impression" value={report.impression || ''} onChange={update('impression')} multiline minRows={4} size="small" disabled={readOnly} sx={fieldSx} />
        {history.length > 0 ? (
          <Box sx={{ mt: 0.5, borderTop: '1px solid #17242a', pt: 1 }}>
            <Typography sx={{ fontSize: 10, color: '#78909c', letterSpacing: 1 }}>HISTORY</Typography>
            {history.slice(0, 5).map(h => (
              <Typography key={h.id} sx={{ fontSize: 9, color: '#607d8b', mt: 0.5 }}>
                v{h.version} · {h.action} · {h.actor || 'unknown'} · {h.createdAt ? new Date(h.createdAt).toLocaleString() : ''}
              </Typography>
            ))}
          </Box>
        ) : null}
      </Box>

      <Divider sx={{ borderColor: '#17242a' }} />
      <Box sx={{ p: 1, display: 'flex', gap: 1 }}>
        <Button size="small" variant="outlined" disabled={saving || loading || isSigned} onClick={() => save(false)} sx={{ fontSize: 10, borderRadius: 0 }}>
          Save Draft
        </Button>
        {!isSigned ? (
          <Button size="small" variant="contained" disabled={saving || loading || readOnly} onClick={() => save(true)} sx={{ fontSize: 10, borderRadius: 0 }}>
            Finalize
          </Button>
        ) : amendmentMode ? (
          <Button size="small" variant="contained" disabled={saving || loading} onClick={amend} sx={{ fontSize: 10, borderRadius: 0 }}>
            Sign Amendment
          </Button>
        ) : (
          <Button size="small" variant="contained" disabled={saving || loading} onClick={() => { setAmendmentMode(true); setMessage('Amendment mode: edit report text, then sign amendment'); }} sx={{ fontSize: 10, borderRadius: 0 }}>
            Start Amendment
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default NativeReportPanel;
